import {Injectable} from "@angular/core";
import {BehaviorSubject} from "rxjs";
import {ADMINISTRATOR_ROLE, ORGANIZER_ROLE} from "@model";

@Injectable({ providedIn:'root' })
export class PermissionService {

  static _currentPermissions: BehaviorSubject<FeaturePermission[] | undefined> = new BehaviorSubject<FeaturePermission[] | undefined>(undefined);


  get currentPermissions(): BehaviorSubject<FeaturePermission[] | undefined> {
    return PermissionService._currentPermissions;
  }

  initPermissions(role?: string): void {
    switch (role) {
      case (ORGANIZER_ROLE):
        this._initOrganiserPermission();
        break;
      case (ADMINISTRATOR_ROLE):
        this._initAdminPermissions();
        break;
      default:
        this._initEmptyPermissions();
        break;
    }
  }

  _initAdminPermissions() {
    let featurePermissions: FeaturePermission[] = [];
    featurePermissions.push(
      new FeaturePermission(Features.OrganisersList, Permission.Edit),
      new FeaturePermission(Features.CategoriesTab, Permission.Edit),
      new FeaturePermission(Features.DiscountsTab, Permission.Edit),
      new FeaturePermission(Features.OrganisersEdit, Permission.Edit),
      new FeaturePermission(Features.Administrators, Permission.Edit),
      new FeaturePermission(Features.AccountStatements, Permission.Edit),
      new FeaturePermission(Features.AccountStatementsDetails, Permission.Edit),
      new FeaturePermission(Features.LeaguesList, Permission.Edit),
      new FeaturePermission(Features.LeagueDetails, Permission.Edit),
      new FeaturePermission(Features.RequestsList, Permission.Edit),
      new FeaturePermission(Features.Users, Permission.Edit));

    this.currentPermissions.next(featurePermissions);
  }

  _initOrganiserPermission() {
    let featurePermissions: FeaturePermission[] = [];
    featurePermissions.push(
      new FeaturePermission(Features.OrganiserProfile, Permission.Edit),
      new FeaturePermission(Features.LeaguesList, Permission.Edit),
      new FeaturePermission(Features.LeagueDetails, Permission.Edit),
      new FeaturePermission(Features.Categories, Permission.Edit),
      new FeaturePermission(Features.RequestsList, Permission.Edit),
      new FeaturePermission(Features.DiscountsList, Permission.Edit));
    this.currentPermissions.next(featurePermissions);
  }

  _initEmptyPermissions() {
    this.currentPermissions.next(undefined);
  }

  emptyPermissions() {
    this.currentPermissions.next(undefined);
  }

  checkPermission(feature: Features, permission: Permission): boolean {
    const featurePermission: FeaturePermission | undefined = this.currentPermissions.getValue()?.find(f => f.feature === feature);
    if (!!featurePermission) {
      switch (permission) {
        case Permission.View:
          return featurePermission.permission !== Permission.None;
        case Permission.Edit:
          return featurePermission.permission === Permission.Edit;
      }
    }
    return false;
  }
}

export enum Features {
  OrganisersList = 'OrganisersList',
  OrganiserProfile = 'OrganiserProfile',
  DiscountsList = 'DiscountsList',
  Categories = 'Categories',
  CategoriesTab = 'CategoriesTab',
  DiscountsTab = 'DiscountsTab',
  OrganisersEdit = 'OrganisersEdit',
  Administrators = 'Administrators',
  AccountStatements = 'AccountStatements',
  AccountStatementsDetails = 'AccountStatementsDetails',
  LeaguesList = 'LeaguesList',
  LeagueDetails = 'LeagueDetails',
  RequestsList = 'RequestsList',
  Users = 'Users',
}

export enum Permission {
  None = 'None',
  View = 'View',
  Edit = 'Edit',
}

export class FeaturePermission {
  feature?: Features;
  permission?: Permission;

  constructor(feature: Features, permission: Permission) {
    this.feature = feature;
    this.permission = permission;
  }
}

